import { Add } from "@carbon/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ReactNode, useMemo } from "react";
import { z } from "zod";

import { companyMembersQueryOptions } from "@/api/companyMembers";
import { formSummariesQueryOptions } from "@/api/formSummaries";
import { listCampaignsQueryOptions } from "@/api/listCampaigns";
import { useAuthStore } from "@/auth/store";
import {
  CampaignCard,
  CampaignDetailsLink,
} from "@/components/admin/campaignCard";
import { CampaignCardSkeleton } from "@/components/admin/campaignCardSkeleton";
import { MemberCard } from "@/components/admin/memberCard";
import { MemberCardSkeleton } from "@/components/admin/memberCardSkeleton";
import { DashboardSectionTitle } from "@/components/admin/sectionTitle";
import { Button, buttonVariants } from "@/components/input/button";
import { Select } from "@/components/input/select";
import {
  CampaignGroupsState,
  PENDING_CAMPAIGN_GROUPS,
  useCampaignGroups,
} from "@/hooks/useCampaignGroups";
import { Campaign } from "@/schemas/campaign";
import { CampaignId } from "@/schemas/campaignId";
import { Employee } from "@/schemas/employee";
import { EmployeeId } from "@/schemas/employeeId";
import {
  dashboardYears,
  memberCampaignsForYear,
} from "@/util/dashboardMembers";
import { lenient } from "@/util/lenient";

// `/admin/campaigns/create` (the campaign wizard) — cast keeps a real <Link>
// (see docs/admin-specs/wave1-dashboard.md).
const CREATE_CAMPAIGN_PATH =
  "/dashboard/admin/campaigns/create" as unknown as "/dashboard/admin/campaigns";

const LATEST_EVALUATIONS_LIMIT = 4;
const MEMBER_SKELETON_COUNT = 8;

// Same column size as the Evaluations list results (specs/013-admin-dashboard-mobile, SC-004), but
// auto-fit: the ≤ 4 latest cards fill the row instead of leaving empty tracks on wide screens.
const EVALUATIONS_GRID = "grid-cards-fit gap-6";

const MEMBERS_GRID = "grid-cards-compact gap-6";

// The "Company members" year lives in the URL so it survives opening a member report and coming
// back (specs/013-admin-dashboard-mobile FR-016). The current year is the default and stays out.
const DashboardSearchSchema = z.object({
  year: lenient(z.number().int().min(1000).max(9999)),
});

export const Route = createFileRoute("/dashboard/_authenticated/admin/")({
  component: RouteComponent,
  validateSearch: DashboardSearchSchema,
  staticData: {
    pageWidth: "wide",
    breadcrumb: ["Dashboard"],
  },
});

function RouteComponent() {
  const { name } = useAuthStore();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  // One "today" per visit: the default year depends on it.
  const today = useMemo(() => new Date(), []);
  const year = search.year ?? today.getFullYear();

  const {
    data: campaignsPage,
    isLoading: isLoadingCampaigns,
    isError: isCampaignsError,
    refetch: refetchCampaigns,
  } = useQuery(listCampaignsQueryOptions());
  const {
    data: membersPage,
    isLoading: isLoadingMembers,
    isError: isMembersError,
    refetch: refetchMembers,
  } = useQuery(companyMembersQueryOptions());
  const { data: forms } = useQuery(formSummariesQueryOptions());

  const latestCampaigns = useMemo(
    () =>
      (campaignsPage?.content ?? [])
        .toSorted(
          (a, b) =>
            new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime(),
        )
        .slice(0, LATEST_EVALUATIONS_LIMIT),
    [campaignsPage?.content],
  );

  // Only the cards on screen need their groups.
  const campaignGroups = useCampaignGroups(
    latestCampaigns,
    membersPage?.content,
    forms,
  );

  const years = useMemo(
    () => dashboardYears(campaignsPage?.content ?? [], today, search.year),
    [campaignsPage?.content, today, search.year],
  );

  // Who has a report for the chosen year, from the campaign list's evaluations (no extra request).
  const campaignByMember = useMemo(
    () =>
      memberCampaignsForYear(
        campaignsPage?.content ?? [],
        membersPage?.content ?? [],
        year,
      ),
    [campaignsPage?.content, membersPage?.content, year],
  );

  return (
    <div
      className="
        flex flex-col gap-6 pb-6
        lg:gap-9
      "
    >
      <div className="text-page-title text-primary-700">Hey {name}!</div>
      <div
        className="
          flex flex-col items-stretch gap-4 rounded-lg bg-primary-50 px-4 py-5
          sm:px-8
          md:flex-row md:items-center md:justify-between
        "
        data-testid="new-evaluation-banner"
      >
        <div className="flex flex-col gap-1">
          <div className="text-h3 text-primary-700">Start a new evaluation</div>
          <div className="text-body2 text-primary-600">
            Choose questions, who evaluates who and define a delivery date for
            submissions
          </div>
        </div>
        <Link
          className={buttonVariants({
            intent: "primary",
            className: `
              w-full shrink-0 justify-center
              md:w-fit
            `,
          })}
          to={CREATE_CAMPAIGN_PATH}
        >
          <Add size={20} />
          Create evaluation
        </Link>
      </div>

      <div className="flex flex-col gap-6">
        <DashboardSectionTitle
          action={
            <Link
              className={buttonVariants({ className: "w-fit shrink-0" })}
              to="/dashboard/admin/campaigns"
            >
              Go to evaluations
            </Link>
          }
        >
          Latest evaluations
        </DashboardSectionTitle>
        {renderLatestEvaluations({
          isLoadingCampaigns,
          isCampaignsError,
          onRetry: () => void refetchCampaigns(),
          latestCampaigns,
          campaignGroups,
        })}
      </div>

      <div className="flex flex-col gap-6">
        <DashboardSectionTitle
          action={
            <Select
              className="w-28"
              hideLabel
              label="Year"
              onValueChange={(value) => {
                const next = Number(value);
                void navigate({
                  search: (previous) => ({
                    ...previous,
                    year: next === today.getFullYear() ? undefined : next,
                  }),
                  replace: true,
                });
              }}
              options={years.map((option) => ({
                value: String(option),
                label: String(option),
              }))}
              triggerClassName="
                min-h-11
                lg:min-h-10
              "
              value={String(year)}
            />
          }
        >
          Company members
        </DashboardSectionTitle>
        {renderCompanyMembers({
          isLoadingMembers,
          isMembersError,
          onRetry: () => void refetchMembers(),
          isLoadingCampaigns,
          members: membersPage?.content,
          campaignByMember,
          year,
        })}
      </div>
    </div>
  );
}

function renderLatestEvaluations(props: {
  isLoadingCampaigns: boolean;
  isCampaignsError: boolean;
  onRetry: () => void;
  latestCampaigns: Campaign[];
  campaignGroups: Map<Campaign["id"], CampaignGroupsState>;
}) {
  const {
    isLoadingCampaigns,
    isCampaignsError,
    onRetry,
    latestCampaigns,
    campaignGroups,
  } = props;

  if (isLoadingCampaigns) {
    return (
      <div className={EVALUATIONS_GRID} role="status">
        <span className="sr-only">Loading evaluations…</span>
        {Array.from({ length: LATEST_EVALUATIONS_LIMIT }, (_, index) => (
          <CampaignCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (isCampaignsError) {
    return (
      <SectionError onRetry={onRetry}>
        Couldn&apos;t load evaluations.
      </SectionError>
    );
  }

  if (latestCampaigns.length === 0) {
    return (
      <div className="text-body2 text-neutral-600">No evaluations yet.</div>
    );
  }

  return (
    <div className={EVALUATIONS_GRID}>
      {latestCampaigns.map((campaign) => (
        <CampaignCard
          action={<CampaignDetailsLink campaignId={campaign.id} />}
          campaign={campaign}
          groups={campaignGroups.get(campaign.id) ?? PENDING_CAMPAIGN_GROUPS}
          key={campaign.id}
        />
      ))}
    </div>
  );
}

function renderCompanyMembers(props: {
  isLoadingMembers: boolean;
  isMembersError: boolean;
  onRetry: () => void;
  isLoadingCampaigns: boolean;
  members: Employee[] | undefined;
  campaignByMember: Map<EmployeeId, CampaignId>;
  year: number;
}) {
  const {
    isLoadingMembers,
    isMembersError,
    onRetry,
    isLoadingCampaigns,
    members,
    campaignByMember,
    year,
  } = props;

  if (isLoadingMembers) {
    return (
      <div className={MEMBERS_GRID} role="status">
        <span className="sr-only">Loading members…</span>
        {Array.from({ length: MEMBER_SKELETON_COUNT }, (_, index) => (
          <MemberCardSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (isMembersError) {
    return (
      <SectionError onRetry={onRetry}>
        Couldn&apos;t load company members.
      </SectionError>
    );
  }

  if (!members || members.length === 0) {
    return (
      <div className="text-body2 text-neutral-600">No company members yet.</div>
    );
  }

  return (
    <div className={MEMBERS_GRID}>
      {members.map((member) => {
        const role = member.role ? { role: member.role } : {};

        // Until campaigns load, don't claim "No evaluation" yet.
        if (isLoadingCampaigns) {
          return (
            <MemberCard
              key={member.uuid}
              layout="row"
              member={member}
              {...role}
            />
          );
        }

        const campaignId = campaignByMember.get(member.uuid);
        if (campaignId === undefined) {
          return (
            <MemberCard
              key={member.uuid}
              layout="row"
              member={member}
              muted
              note={`No evaluation in ${year}`}
              {...role}
            />
          );
        }

        return (
          <Link
            className="
              min-w-0 rounded-lg
              hover:bg-primary-50
            "
            key={member.uuid}
            params={{ campaignId, employeeId: member.uuid }}
            to="/dashboard/admin/reports/$campaignId/$employeeId"
          >
            <MemberCard layout="row" member={member} {...role} />
          </Link>
        );
      })}
    </div>
  );
}

function SectionError({
  children,
  onRetry,
}: {
  children: ReactNode;
  onRetry: () => void;
}) {
  return (
    <div
      className="
        flex flex-col items-start gap-4 rounded-lg border-2 border-dashed
        border-error-100 p-6
      "
    >
      <div className="text-body2 text-error-300">{children}</div>
      <Button
        className="
          min-h-11
          lg:min-h-10
        "
        onClick={onRetry}
        size="sm"
        type="button"
      >
        Retry
      </Button>
    </div>
  );
}
